import React, { Component } from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import ListAuditTrailView from "./ListAuditTrailView";
import { getAuditTrails } from "../../../services";
import { showErrorNotification, getProp } from "../../../helpers";

class ListAuditTrailContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      auditTrails: [],
      isLoading: false,
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      startDate: this.getDefaultStartDate(),
      endDate: new Date().toISOString().split("T")[0],
      // Filter states
      searchText: "",
      selectedEvents: [],
      selectedStatus: [],
      selectedUserType: [],
      selectedResourceType: [],
      // Filter options (populated from API data)
      eventOptions: [],
      statusOptions: [],
      userTypeOptions: [],
      resourceTypeOptions: []
    };
  }

  componentDidMount() {
    this.fetchInitialData();
  }

  componentWillUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  fetchInitialData = () => {
    // First, fetch without filters to populate filter options
    this.setState({ isLoading: true });

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30); // Get last 30 days
    const formattedStartDate = startDate.toISOString().split("T")[0];

    getAuditTrails(formattedStartDate, this.state.endDate, 1, {
      clinic_id: this.props.clinicId
    })
      .then(response => {
        const getSuccess = getProp(["data", "success"]);
        const getData = getProp(["data", "data"]);

        if (getSuccess(response)) {
          const data = getData(response);
          const auditTrails = data.audit_trails || [];

          // Update filter options from broader dataset
          this.updateFilterOptions(auditTrails);
        }

        // Now fetch with current filters
        this.fetchAuditTrails();
      })
      .catch(() => {
        this.fetchAuditTrails();
      });
  };

  getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  };

  fetchAuditTrails = (page = 1) => {
    this.setState({ isLoading: true, currentPage: page });

    const filters = this.buildFilters();

    getAuditTrails(this.state.startDate, this.state.endDate, page, filters)
      .then(response => {
        this.setState({ isLoading: false });
        const getSuccess = getProp(["data", "success"]);
        const getData = getProp(["data", "data"]);
        const getErrors = getProp(["data", "errors"]);

        if (getSuccess(response)) {
          const data = getData(response);
          const auditTrails = data.audit_trails || [];

          // Apply client-side search filter
          let filteredTrails = auditTrails;
          if (this.state.searchText && this.state.searchText.trim()) {
            const searchTerm = this.state.searchText.toLowerCase().trim();

            filteredTrails = auditTrails.filter(trail => {
              // Simple string matching across all relevant fields
              const searchableText = [
                trail.user?.name,
                trail.user?.type,
                trail.patient?.name,
                trail.key,
                trail.event_status,
                trail.description,
                trail.user_agent,
                trail.ip_address,
                trail.resource?.type,
                // Add parameters values
                ...(trail.parameters ? Object.values(trail.parameters) : [])
              ]
                .filter(Boolean) // Remove null/undefined
                .map(val => String(val).toLowerCase()) // Convert to lowercase
                .join(" "); // Join into single searchable string

              return searchableText.includes(searchTerm);
            });
          }

          this.setState({
            auditTrails: filteredTrails,
            totalPages: data.total_pages || 0,
            totalRecords: data.total_records || 0
          });

          // Update filter options from the original data
          this.updateFilterOptions(auditTrails);
        } else if (getErrors(response)) {
          const { errors } = response.data;
          let error = errors;
          if (Array.isArray(errors)) {
            [error] = errors;
          }
          showErrorNotification(error.title || "Failed to fetch audit trails");
        }
      })
      .catch(() => {
        this.setState({ isLoading: false });
        showErrorNotification("Failed to fetch audit trails");
      });
  };

  buildFilters = () => {
    const {
      searchText,
      selectedEvents,
      selectedStatus,
      selectedUserType,
      selectedResourceType
    } = this.state;
    const { clinicId } = this.props;

    const filters = {};

    if (clinicId) filters.clinic_id = clinicId;
    if (searchText && searchText.trim()) {
      filters.search_text = searchText.trim();
    }
    if (selectedEvents.length > 0) {
      filters.event_keys = selectedEvents.join(",");
    }
    if (selectedStatus.length > 0) {
      filters.event_status = selectedStatus.join(",");
    }
    if (selectedUserType.length > 0) {
      filters.user_type = selectedUserType.join(",");
    }
    if (selectedResourceType.length > 0) {
      filters.resource_type = selectedResourceType.join(",");
    }

    return filters;
  };

  updateFilterOptions = auditTrails => {
    const eventOptions = [
      ...new Set(auditTrails.map(trail => trail.key).filter(Boolean))
    ];

    // Fixed status options with display names
    const statusOptions = [
      { value: "SUCCESS", label: "Success" },
      { value: "FAILED", label: "Failure" }
    ];

    // Fixed user type options
    const userTypeOptions = [
      { value: "owner", label: "Owner" },
      { value: "consultant", label: "Consultant" },
      { value: "staff", label: "Staff" },
      { value: "admin", label: "Admin" }
    ];

    const resourceTypeOptions = [
      ...new Set(auditTrails.map(trail => trail.resource?.type).filter(Boolean))
    ];

    this.setState({
      eventOptions,
      statusOptions,
      userTypeOptions,
      resourceTypeOptions
    });
  };

  validateDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const maxRange = 90 * 24 * 60 * 60 * 1000;

    if (endDate - startDate > maxRange) {
      showErrorNotification("Date range cannot exceed 90 days");
      return false;
    }

    if (startDate > endDate) {
      showErrorNotification("Start date cannot be after end date");
      return false;
    }

    if (endDate > new Date()) {
      showErrorNotification("End date cannot be in the future");
      return false;
    }

    return true;
  };

  handleDateRangeChange = (startDate, endDate) => {
    if (!this.validateDateRange(startDate, endDate)) {
      return;
    }

    this.setState({ startDate, endDate, currentPage: 1 }, () => {
      this.fetchAuditTrails(1);
    });
  };

  handlePageChange = page => {
    this.fetchAuditTrails(page);
  };

  handleRefresh = () => {
    this.fetchAuditTrails(this.state.currentPage);
  };

  handleSearchChange = searchText => {
    this.setState({ searchText, currentPage: 1 });

    // Debounce search to avoid too many API calls
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.fetchAuditTrails(1);
    }, 200);
  };

  handleFilterChange = (filterType, values) => {
    this.setState({ [filterType]: values, currentPage: 1 }, () => {
      this.fetchAuditTrails(1);
    });
  };

  handleQuickDateRange = days => {
    const endDate = new Date().toISOString().split("T")[0];
    let startDate;

    if (days === 0) {
      // Today - same start and end date
      startDate = endDate;
    } else {
      // Past days
      const date = new Date();
      date.setDate(date.getDate() - days);
      [startDate] = date.toISOString().split("T");
    }

    // Validate the date range before setting
    if (!this.validateDateRange(startDate, endDate)) {
      return;
    }

    this.setState(
      {
        startDate,
        endDate,
        currentPage: 1
      },
      () => {
        this.fetchAuditTrails(1);
      }
    );
  };

  handleClearFilters = () => {
    const defaultStartDate = this.getDefaultStartDate();
    const defaultEndDate = new Date().toISOString().split("T")[0];

    // Validate the default date range
    if (!this.validateDateRange(defaultStartDate, defaultEndDate)) {
      return;
    }

    this.setState(
      {
        searchText: "",
        selectedEvents: [],
        selectedStatus: [],
        selectedUserType: [],
        selectedResourceType: [],
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        currentPage: 1
      },
      () => {
        this.fetchAuditTrails(1);
      }
    );
  };

  render() {
    const {
      auditTrails,
      isLoading,
      currentPage,
      totalPages,
      totalRecords,
      startDate,
      endDate,
      searchText,
      selectedEvents,
      selectedStatus,
      selectedUserType,
      selectedResourceType,
      eventOptions,
      statusOptions,
      userTypeOptions,
      resourceTypeOptions
    } = this.state;

    return (
      <Spin spinning={isLoading}>
        <ListAuditTrailView
          auditTrails={auditTrails}
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          startDate={startDate}
          endDate={endDate}
          searchText={searchText}
          selectedEvents={selectedEvents}
          selectedStatus={selectedStatus}
          selectedUserType={selectedUserType}
          selectedResourceType={selectedResourceType}
          eventOptions={eventOptions}
          statusOptions={statusOptions}
          userTypeOptions={userTypeOptions}
          resourceTypeOptions={resourceTypeOptions}
          onDateRangeChange={this.handleDateRangeChange}
          onPageChange={this.handlePageChange}
          onRefresh={this.handleRefresh}
          onSearchChange={this.handleSearchChange}
          onFilterChange={this.handleFilterChange}
          onQuickDateRange={this.handleQuickDateRange}
          onClearFilters={this.handleClearFilters}
        />
      </Spin>
    );
  }
}

ListAuditTrailContainer.propTypes = {
  clinicId: PropTypes.string.isRequired
};

export default ListAuditTrailContainer;
